/**
 * 에러 인박스 백엔드 보고 클라이언트.
 *
 * - errorTracker가 만든 EnrichedError를 받아 `/api/error-logs/report/`로 POST한다.
 * - info 레벨은 인박스 노이즈 방지를 위해 보내지 않는다.
 * - 자기 자신(POST report)이 실패해 다시 보고되는 self-loop를 차단한다.
 * - 같은 메시지+첫 stack 라인 조합은 5초 내 1회만 보낸다 (디바운스).
 * - POST 실패는 silent (logger.warn).
 */

import { AXIOS_INSTANCE } from '@/lib/api/orval/custom-instance';
import { logger } from '@/lib/utils/logger';

import type { EnrichedError } from './errorTracker';

const REPORT_PATH = '/api/error-events/report/';
const DEDUP_WINDOW_MS = 5000;
const MAX_DEDUP_KEYS = 200;

const seen = new Map<string, number>();

function makeKey(e: EnrichedError): string {
  const stack = e.debugInfo.stack ?? '';
  const firstLine = stack.split('\n')[0] ?? '';
  return `${e.error.message}::${firstLine}`;
}

function shouldSkip(e: EnrichedError): boolean {
  if (e.severity === 'info') {return true;}

  const endpoint = (e.context.endpoint ?? '').toString();
  if (endpoint.includes(REPORT_PATH)) {return true;}
  const url = (e.context.url ?? '').toString();
  if (url.includes(REPORT_PATH)) {return true;}

  const key = makeKey(e);
  const now = Date.now();
  const last = seen.get(key);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) {return true;}
  seen.set(key, now);
  if (seen.size > MAX_DEDUP_KEYS) {seen.clear();}

  return false;
}

export async function reportErrorToServer(e: EnrichedError): Promise<void> {
  if (shouldSkip(e)) {return;}

  const ua =
    e.context.userAgent ??
    (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const reqPath =
    e.context.url ??
    (typeof window !== 'undefined' ? window.location.href : '');

  const payload = {
    errorType: e.error.name || 'Error',
    errorMessage: e.error.message,
    severity: e.severity,
    traceback: e.debugInfo.stack ?? '',
    requestPath: reqPath,
    userAgent: ua,
    extra: {
      component: e.context.component,
      action: e.context.action,
      function: e.context.function,
      endpoint: e.context.endpoint,
      ...(e.context.additionalData ?? {}),
    },
  };

  try {
    await AXIOS_INSTANCE.post(REPORT_PATH, payload);
  } catch (err) {
    logger.warn('[error-inbox] 백엔드 보고 실패', err);
  }
}

/** 테스트용 — 디바운스 캐시 비우기 */
export function __clearReporterDedup(): void {
  seen.clear();
}
